import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  UserPlus, 
  Mail, 
  Trash2, 
  X,
  Users,
  Send,
  Star,
  MessageCircle
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import LoadingSpinner from './LoadingSpinner';

const BetaTesterManagement = ({ isOpen, onClose, adminToken }) => {
  const [betaTesters, setBetaTesters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    practiceName: '',
    email: ''
  });

  const [createdPassword, setCreatedPassword] = useState(null);

  useEffect(() => {
    if (isOpen) {
      loadBetaTesters();
    }
  }, [isOpen]);

  const loadBetaTesters = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-testers`, {
        headers: {
          'Authorization': `Bearer ${adminToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setBetaTesters(data.beta_testers || []);
      } else {
        throw new Error('Failed to load beta testers');
      }
    } catch (error) {
      console.error('Error loading beta testers:', error);
      toast({
        title: "Error",
        description: "Failed to load beta testers",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCreateBetaTester = async (e) => {
    e.preventDefault();

    if (!formData.practiceName || !formData.email) {
      toast({
        title: "Validation Error",
        description: "Please fill in all fields",
        variant: "destructive"
      });
      return;
    }

    setCreating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-testers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${adminToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        const data = await response.json();
        setCreatedPassword(data.password);
        
        toast({
          title: "Success",
          description: "Beta tester created successfully!"
        });

        setFormData({ practiceName: '', email: '' });
        loadBetaTesters();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to create beta tester');
      }
    } catch (error) {
      console.error('Error creating beta tester:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to create beta tester",
        variant: "destructive"
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSendWelcomeEmail = async (practiceId, email) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-testers/${practiceId}/send-welcome`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: `Welcome email sent to ${email}`
        });
      } else {
        throw new Error('Failed to send email');
      }
    } catch (error) {
      console.error('Error sending welcome email:', error);
      toast({
        title: "Error",
        description: "Failed to send welcome email",
        variant: "destructive"
      });
    }
  };

  const handleSendReviewRequest = async (practiceId, email) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-testers/${practiceId}/send-review-request`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: `Review request email sent to ${email}`
        });
      } else {
        throw new Error('Failed to send review request');
      }
    } catch (error) {
      console.error('Error sending review request:', error);
      toast({
        title: "Error",
        description: "Failed to send review request",
        variant: "destructive"
      });
    }
  };

  const handleSendCheckin = async (practiceId, email) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-testers/${practiceId}/send-checkin`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: `2-week check-in email sent to ${email}`
        });
        loadBetaTesters(); // Reload to show updated status
      } else {
        throw new Error('Failed to send check-in email');
      }
    } catch (error) {
      console.error('Error sending check-in email:', error);
      toast({
        title: "Error",
        description: "Failed to send check-in email",
        variant: "destructive"
      });
    }
  };

  const handleDeleteBetaTester = async (practiceId, practiceName) => {
    if (!window.confirm(`Are you sure you want to delete beta tester "${practiceName}"?`)) {
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-testers/${practiceId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${adminToken}`
          }
        }
      );

      if (response.ok) {
        toast({
          title: "Success",
          description: "Beta tester deleted successfully"
        });
        loadBetaTesters();
      } else {
        throw new Error('Failed to delete beta tester');
      }
    } catch (error) {
      console.error('Error deleting beta tester:', error);
      toast({
        title: "Error",
        description: "Failed to delete beta tester",
        variant: "destructive"
      });
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden flex flex-col" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="p-6 border-b flex items-center justify-between bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
          <div className="flex items-center space-x-3">
            <Users className="h-6 w-6" />
            <h2 className="text-2xl font-bold">Beta Tester Management</h2>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-white hover:bg-white/20"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {/* Add Beta Tester Button */}
          {!showAddForm && !createdPassword && (
            <div className="mb-6">
              <Button
                onClick={() => setShowAddForm(true)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Add Beta Tester
              </Button>
            </div>
          )}

          {/* Add Form */}
          {showAddForm && (
            <Card className="mb-6 border-purple-200">
              <CardHeader className="bg-purple-50">
                <CardTitle className="flex items-center justify-between">
                  <span>Add New Beta Tester</span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowAddForm(false);
                      setFormData({ practiceName: '', email: '' });
                    }}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-6">
                <form onSubmit={handleCreateBetaTester} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Practice Name *
                    </label>
                    <Input
                      value={formData.practiceName}
                      onChange={(e) => setFormData({ ...formData, practiceName: e.target.value })}
                      placeholder="e.g., Smith Dental Practice"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address *
                    </label>
                    <Input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="e.g., dr.smith@example.com"
                      required
                    />
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <Button
                      type="submit"
                      disabled={creating}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      {creating ? (
                        <>
                          <LoadingSpinner className="h-4 w-4 mr-2" />
                          Creating...
                        </>
                      ) : (
                        <>
                          <UserPlus className="h-4 w-4 mr-2" />
                          Create Beta Tester
                        </>
                      )}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => {
                        setShowAddForm(false);
                        setFormData({ practiceName: '', email: '' });
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          )}

          {/* Password Display */}
          {createdPassword && (
            <Card className="mb-6 border-green-200 bg-green-50">
              <CardHeader>
                <CardTitle className="text-green-800">Beta Tester Created Successfully!</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <p className="text-sm text-gray-700">
                    Please save this password. It will not be shown again.
                  </p>
                  <div className="bg-white p-4 rounded border border-green-300">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-gray-500 mb-1">Generated Password:</p>
                        <p className="text-lg font-mono font-bold text-gray-900">{createdPassword}</p>
                      </div>
                      <Button
                        size="sm"
                        onClick={() => {
                          navigator.clipboard.writeText(createdPassword);
                          toast({
                            title: "Copied!",
                            description: "Password copied to clipboard"
                          });
                        }}
                      >
                        Copy
                      </Button>
                    </div>
                  </div>
                  <Button
                    onClick={() => {
                      setCreatedPassword(null);
                      setShowAddForm(false);
                    }}
                    variant="outline"
                    className="w-full"
                  >
                    Close
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Beta Testers List */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Beta Testers ({betaTesters.length})</span>
                <Badge variant="outline" className="bg-purple-100 text-purple-800">
                  Lifetime Access
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-8">
                  <LoadingSpinner />
                  <p className="mt-2 text-gray-600">Loading beta testers...</p>
                </div>
              ) : betaTesters.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <Users className="h-16 w-16 mx-auto mb-4 text-gray-300" />
                  <h3 className="text-lg font-medium mb-2">No Beta Testers Yet</h3>
                  <p className="text-sm">Create your first beta tester account</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {betaTesters.map((tester) => (
                    <div
                      key={tester.id}
                      className="flex flex-col xl:flex-row xl:items-center justify-between gap-3 p-4 border border-gray-200 rounded-lg hover:bg-gray-50"
                    >
                      <div className="flex-1 min-w-0">
                        <h4 className="font-semibold text-gray-900">{tester.name}</h4>
                        <p className="text-sm text-gray-600">{tester.email}</p>
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          <Badge variant="outline" className="bg-purple-100 text-purple-800 text-xs">
                            Beta Tester
                          </Badge>
                          <span className="text-xs text-gray-500">
                            Created: {new Date(tester.createdAt || tester.created_at).toLocaleDateString()}
                          </span>
                          {tester.betaProgram && (
                            <Badge variant="outline" className={`text-xs ${tester.betaProgram.lifetime ? 'bg-amber-100 text-amber-800' : 'bg-sky-100 text-sky-800'}`} data-testid={`beta-status-${tester.id}`}>
                              {tester.betaProgram.lifetime ? '★ Free for life'
                                : `90-day beta · ends ${tester.subscription?.trialEndsAt ? new Date(tester.subscription.trialEndsAt).toLocaleDateString() : '—'}`}
                            </Badge>
                          )}
                          {tester.checkin_sent && (
                            <Badge variant="outline" className="bg-green-100 text-green-800 text-xs">
                              ✓ Check-in Sent
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {tester.betaProgram && !tester.betaProgram.lifetime && (
                          <Button size="sm" variant="outline" className="text-amber-700 hover:text-amber-800" data-testid={`beta-grant-lifetime-${tester.id}`}
                            title="They sent their testimonial, photo and 5-star review"
                            onClick={async () => {
                              if (!window.confirm(`Make ${tester.name} free for life? Only do this after you have their testimonial, photo and 5-star review.`)) return;
                              const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/beta-testers/${tester.id}/grant-lifetime`, { method: 'POST', headers: { Authorization: `Bearer ${adminToken}` } });
                              if (r.ok) loadBetaTesters(); else alert('Could not update. Please try again.');
                            }}>
                            <Star className="h-4 w-4 mr-1" /> Grant Lifetime
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendWelcomeEmail(tester.id, tester.email)}
                          className="text-blue-600 hover:text-blue-700"
                        >
                          <Send className="h-4 w-4 mr-1" />
                          Send Welcome
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendCheckin(tester.id, tester.email)}
                          className="text-purple-600 hover:text-purple-700"
                          title="Send 2-week check-in email"
                        >
                          <MessageCircle className="h-4 w-4 mr-1" />
                          Check-In
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleSendReviewRequest(tester.id, tester.email)}
                          className="text-green-600 hover:text-green-700"
                        >
                          <Star className="h-4 w-4 mr-1" />
                          Request Review
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleDeleteBetaTester(tester.id, tester.name)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default BetaTesterManagement;
